<script setup lang="ts">
import { ref, watch, toRefs, toRef, computed, onMounted } from 'vue';
const emit = defineEmits(['update:modelValue', 'change']);

interface List {
  id: number;
  label: string;
}

// 定义 props 类型
interface Props {
  modelValue: number; //当前值
  class?: string;
  fontsize?: number; // 字体大小
  list: List[]; // 列表
  defaultLabel?: string; // 默认显示的label
  disabled?: boolean; // 是否禁用
}

// 使用 withDefaults 设置默认值
const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  class: '',
  fontsize: 16,
  list: () => [],
  defaultLabel: '其他',
  disabled: false,
});

const { modelValue, list, defaultLabel, disabled } = toRefs(props);
const rootClass = toRef(props, 'class');

const currentLabel = ref('');
const isShowPopover = ref(false); //是否显示下拉框

const currentValue = computed({
  get: () => props.modelValue,
  set: (newValue) => {
    currentLabel.value =
      list.value.find((item) => item.id === newValue)?.label ||
      defaultLabel.value;
    emit('update:modelValue', newValue);
  },
});

watch(currentValue, (newValue) => {
  currentLabel.value =
    list.value.find((item) => item.id === newValue)?.label ||
    defaultLabel.value;
});

const chooseItem = (id: number, label: string) => {
  currentValue.value = id;
  isShowPopover.value = false;
  emit('change', id, label);
};

const hideComponent = () => {
  isShowPopover.value = false;
};

const mouseenter = () => {
  if (props.disabled) return;
  isShowPopover.value = true;
};

const mouseleave = () => {
  if (props.disabled) return;
  isShowPopover.value = false;
};

onMounted(() => {
  currentLabel.value =
    list.value.find((item) => item.id === modelValue.value)?.label ||
    defaultLabel.value;
});
</script>

<template>
  <div
    :class="rootClass"
    class="fr-ac pos-r"
    @mouseenter="mouseenter"
    @mouseleave="mouseleave"
    v-click-outside="hideComponent"
  >
    <div class="fr-ac pointer" :class="disabled ? 'opa4' : ''">
      <div
        class="fr-ac-jc select-box"
        :class="{
          'select-box-active': list.some((item) => item.id === currentValue),
        }"
      >
        <IconFont
          v-if="list.some((item) => item.id === currentValue)"
          name="icon_dagou"
          :size="12"
          color="#FFFFFF"
        />
      </div>
      <div
        class="fs-16 ml-8 color-white"
        :style="{ 'font-size': fontsize + 'px' }"
      >
        {{ currentLabel }}
      </div>
      <IconFont
        class="ml-5 opa5"
        :class="isShowPopover ? 'icon-pull-active' : 'icon-pull-unactive'"
        name="icon_pull"
        :size="10"
        color="#333"
      />
    </div>
    <div class="popover-space" v-if="isShowPopover"></div>
    <div class="popover-box" v-if="isShowPopover">
      <div
        class="fr-ac mb-16 pointer"
        v-for="(item, index) in list"
        :key="index"
        @click="chooseItem(item.id, item.label)"
      >
        <div
          class="fr-ac-jc select-box"
          :class="{ 'select-box-active': currentValue === item.id }"
        >
          <IconFont
            v-if="currentValue === item.id"
            name="icon_dagou"
            :size="12"
            color="#FFFFFF"
          />
        </div>
        <div
          class="fs-16 ml-10 color-white"
          style="flex-shrink: 0"
          :style="{ 'font-size': fontsize + 'px' }"
        >
          {{ item.label }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.select-box {
  width: 18px;
  height: 18px;
  background: rgba(55, 133, 255, 0.3);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
}

.select-box-active {
  background: #3785ff;
  border: 1px solid #3785ff;
}

.icon-pull-active {
  animation: rotate180 0.5s forwards;
}

.icon-pull-unactive {
  animation: rotate-180 0.5s forwards;
}

@keyframes rotate180 {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(180deg);
  }
}

@keyframes rotate-180 {
  from {
    transform: rotate(180deg);
  }

  to {
    transform: rotate(0deg);
  }
}

.popover-space {
  position: absolute;
  min-width: 136px;
  height: 100px;
  top: 18px;
  left: -23px;
}

.popover-box {
  position: absolute;
  z-index: 9;
  left: 50%;
  top: 40px;
  transform: translateX(-50%);
  padding: 23px 23px 7px;
  min-width: 136px;
  border-radius: var(--border-radius);
  // background: linear-gradient(180deg, #111b37 0%, #091023 100%);
  // background: rgba(55, 133, 255, 0.3);
  background: var(--bg-color-white);
  box-shadow: var(--selector-box-shadow);
  backdrop-filter: blur(10px);
}

.popover-box::before {
  content: '';
  position: absolute;
  left: 50%;
  top: -8px;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid var(--bg-color-white);
}
</style>
